import {
  Component,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnInit,
} from '@angular/core';
import {
  CourseTrack,
  QuizAnswerResponse,
  QuizQuestion,
  QuizResultsStorage,
} from '../../shared/models';
import { AcademyNavigationService } from '../../services/academy-navigation.service';
import { AcademyCycleSignUpService } from '../../services/academy-cycle-sign-up.service';
import { AcademyQuizService } from '../../services/academy-quiz.service';
import {
  isPartOfLearningTrack,
  learningTrackHeaderTitle,
  learningTrackCoursesCompleted,
  learningTrackCoursesTotal,
  hasCompletedLearningTrack,
} from '../../shared/course-track.utils';

@Component({
  selector: 'ut-academy-quiz-results-container',
  templateUrl: './quiz-results-container.component.html',
  styleUrls: ['./quiz-results-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class QuizResultsContainerComponent implements OnInit {
  @Input() trackId: number | null = null;
  @Input() courseTrack: CourseTrack | null = null;
  @Input() useRedesignCatalog = false;
  @Input() user: any = null;

  sortedResults: QuizAnswerResponse[] = [];
  passed = false;
  showSignUpConfirmation = false;

  get isPartOfLearningTrack(): boolean {
    return isPartOfLearningTrack(this.courseTrack, this.useRedesignCatalog);
  }

  get headerTitle(): string {
    return learningTrackHeaderTitle(this.courseTrack, this.useRedesignCatalog);
  }

  get trackCoursesCompleted(): number {
    return learningTrackCoursesCompleted(this.courseTrack);
  }

  get trackCoursesTotal(): number {
    return learningTrackCoursesTotal(this.courseTrack);
  }

  get totalQuestions(): number {
    return this.sortedResults.length;
  }

  get isLockedByOrder(): boolean {
    return !!this.courseTrack?.user_is_locked_by_learning_track_order;
  }

  get bannerImage(): string {
    return this.passed
      ? '/utest-static/assets/images/academy-quiz-banner-success.png'
      : '/utest-static/assets/images/academy-quiz-banner-fail.png';
  }

  get bannerAlt(): string {
    return this.passed ? 'Excellent Work' : 'Almost There';
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private academyNavigationService: AcademyNavigationService,
    private cycleSignUpService: AcademyCycleSignUpService,
    private academyQuizService: AcademyQuizService,
  ) {}

  ngOnInit(): void {
    this.passed = !!this.courseTrack?.user_has_passed_quiz;
    this.loadResults();
    this.refreshCourseTrack();
  }

  getQuestion(result: QuizAnswerResponse): QuizQuestion | null {
    const questions: QuizQuestion[] = this.courseTrack?.quiz?.questions || [];
    return questions.find(q => q.id === result.quiz_question_id) || null;
  }

  onReviewLesson(courseSlug: string): void {
    if (this.courseTrack) {
      this.academyNavigationService.goToLesson(this.courseTrack, courseSlug);
    }
  }

  onQuizClick(): void {
    if (this.courseTrack) {
      this.academyNavigationService.goToQuiz(this.courseTrack);
    }
  }

  private refreshCourseTrack(): void {
    if (!this.trackId) return;

    this.academyQuizService.getTrackWithQuiz(this.trackId).subscribe({
      next: (courseTrack) => {
        this.courseTrack = courseTrack;
        this.passed = !!courseTrack.user_has_passed_quiz;
        this.cdr.markForCheck();
      },
    });
  }

  private loadResults(): void {
    if (!this.trackId) return;

    const storageKey = `courseTrackQuiz${this.trackId}`;
    const stored = localStorage.getItem(storageKey);
    if (!stored) return;

    try {
      const parsed: QuizResultsStorage = JSON.parse(stored);
      if (parsed?.userChoice) {
        this.sortedResults = [...parsed.userChoice].sort(
          (a, b) => a.quiz_question_id - b.quiz_question_id,
        );
      }
    } catch {
      // Invalid stored data — leave results empty
    }
  }

  onBackToTracks(): void {
    this.academyNavigationService.goToAcademyHome();
  }

  get showNextCourse(): boolean {
    if (!this.passed) return false;
    if (hasCompletedLearningTrack(this.courseTrack)) return false;
    return !!this.courseTrack?.next_course_track_id;
  }

  get showReturnToTracks(): boolean {
    if (!this.passed) return false;
    if (hasCompletedLearningTrack(this.courseTrack)) return true;
    return !this.courseTrack?.next_course_track_id;
  }

  onNextCourse(): void {
    this.academyNavigationService.goToNextCourse(this.courseTrack);
  }

  onLessonClick(lesson: any): void {
    if (this.courseTrack && lesson?.slug) {
      this.academyNavigationService.goToLesson(this.courseTrack, lesson.slug);
    }
  }

  async onCycleSignUp(): Promise<void> {
    const result = await this.cycleSignUpService.signUp(this.courseTrack, this.user, this.cdr);
    if (result) {
      this.courseTrack = result.courseTrack;
      this.showSignUpConfirmation = result.showSignUpConfirmation;
    }
  }

  onCycleSignUpConfirm(): void {
    this.showSignUpConfirmation = false;
    this.cdr.markForCheck();
  }
}
